import React, { useState, useEffect } from 'react';
import { UserPlus, UserMinus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { followsService } from '../services/followsService';

interface FollowButtonProps {
  userId: string;
  username?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'outline';
  showIcon?: boolean;
  onFollowChange?: (isFollowing: boolean) => void;
}

const FollowButton: React.FC<FollowButtonProps> = ({ 
  userId, 
  username,
  size = 'md', 
  variant = 'primary',
  showIcon = true,
  onFollowChange 
}) => {
  const { user } = useAuth();
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user && userId && user.id !== userId) {
      checkFollowStatus();
    }
  }, [user, userId]);

  const checkFollowStatus = async () => {
    try {
      const following = await followsService.isFollowing(userId);
      setIsFollowing(following);
    } catch (err) {
      console.error('Error checking follow status:', err);
    }
  };

  const handleFollow = async () => {
    if (!user) return;
    
    setLoading(true);
    setError('');

    try {
      // Re-check current follow status to ensure we have the latest state
      const currentFollowStatus = await followsService.isFollowing(userId);
      setIsFollowing(currentFollowStatus);
      
      if (currentFollowStatus) {
        await followsService.unfollowUser(userId);
        setIsFollowing(false);
      } else {
        await followsService.followUser(userId);
        setIsFollowing(true);
      }
      
      if (onFollowChange) {
        onFollowChange(!currentFollowStatus);
      }
    } catch (err) {
      // Handle specific Supabase unique constraint violation
      if (err instanceof Error && err.message.includes('23505')) {
        setError('Ya estás siguiendo a este usuario');
        setIsFollowing(true);
        if (onFollowChange) {
          onFollowChange(true);
        }
      } else {
        setError(err instanceof Error ? err.message : 'Error al seguir usuario');
      }
      console.error('Follow error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Don't show button for own profile or if not logged in
  if (!user || user.id === userId) {
    return null;
  }

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };

  const baseClasses = `
    inline-flex items-center gap-2 font-medium rounded-lg transition-all duration-200 
    disabled:opacity-50 disabled:cursor-not-allowed
    ${sizeClasses[size]}
  `;

  const variantClasses = {
    primary: isFollowing 
      ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
      : 'bg-purple-600 text-white hover:bg-purple-700 hover:scale-105 shadow-lg hover:shadow-xl',
    outline: isFollowing
      ? 'border border-gray-300 text-gray-700 hover:bg-gray-50'
      : 'border border-purple-600 text-purple-600 hover:bg-purple-50'
  };

  return (
    <div>
      <button
        onClick={handleFollow}
        disabled={loading}
        className={`${baseClasses} ${variantClasses[variant]}`}
        title={isFollowing ? `Dejar de seguir a ${username || 'usuario'}` : `Seguir a ${username || 'usuario'}`}
      >
        {loading ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
        ) : (
          <>
            {showIcon && (
              isFollowing ? <UserMinus size={16} /> : <UserPlus size={16} />
            )}
            <span>
              {isFollowing ? 'Siguiendo' : 'Seguir'}
            </span>
          </>
        )}
      </button>
      
      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}
    </div>
  );
};

export default FollowButton;